<template>
  <div class="page">
    <div class="heading"><div><h1>存档备份</h1><p>设置自动备份和清理时间，避免长期积累的备份占满磁盘。</p></div><el-button :loading="loading" @click="refresh">刷新</el-button></div>
    <div class="panel">
      <h3>每日自动计划</h3>
      <p class="hint">以下时间均为服务器时间（北京时间，UTC+8）。自动备份只在没有玩家在线且能确认游戏状态时执行；有人在线会跳过并留下记录。</p>
      <el-form label-position="top" class="form" @submit.prevent="save">
        <div class="setting-row"><div><strong>每天自动备份</strong><p>停止写入、备份存档，再恢复服务</p></div><el-switch v-model="settings.backupEnabled" /></div>
        <el-form-item v-if="settings.backupEnabled" label="备份时间"><el-time-picker v-model="settings.backupAt" format="HH:mm" value-format="HH:mm" placeholder="选择备份时间" /></el-form-item>
        <div class="setting-row"><div><strong>每天清理旧备份</strong><p>只清理超过保留期限的旧备份，始终保留最新一份</p></div><el-switch v-model="settings.cleanupEnabled" /></div>
        <el-form-item v-if="settings.cleanupEnabled" label="清理时间"><el-time-picker v-model="settings.cleanupAt" format="HH:mm" value-format="HH:mm" placeholder="选择清理时间" /></el-form-item>
        <el-form-item label="备份保留天数"><el-input-number v-model="settings.retentionDays" :min="1" :max="365" /><span class="inline-note">天</span></el-form-item>
        <el-button type="primary" :loading="saving" @click="save">保存定时计划</el-button>
      </el-form>
    </div>
    <div class="panel">
      <div class="heading-small"><h3>手动操作</h3><div class="action-buttons">
        <el-button :loading="running==='backup'" :disabled="!!running" type="primary" plain @click="submit('backup')">立即备份</el-button>
        <el-button :loading="running==='cleanup'" :disabled="!!running" @click="submit('cleanup')">立即清理过期备份</el-button>
      </div></div>
      <p class="hint">任务提交后可在下方查看执行结果。备份存放在服务器的 /opt/stradewAI/backups。</p>
    </div>
    <div class="panel">
      <div class="heading-small"><h3>执行记录</h3><el-button size="small" @click="history">刷新记录</el-button></div>
      <el-table :data="entries" class="record-table" empty-text="暂无执行记录">
        <el-table-column label="任务" min-width="95"><template #default="{row}">{{row.jobType==='BACKUP'?'存档备份':'清理备份'}}</template></el-table-column>
        <el-table-column label="状态" min-width="88"><template #default="{row}"><el-tag size="small" :type="row.status==='SUCCESS'?'success':row.status==='RUNNING'?'warning':row.status==='SKIPPED'?'info':'danger'">{{({SUCCESS:'成功',RUNNING:'执行中',FAILED:'失败',SKIPPED:'已跳过'})[row.status] || row.status}}</el-tag></template></el-table-column>
        <el-table-column prop="startedAt" label="开始时间" min-width="165"><template #default="{row}">{{ formatDate(row.startedAt) }}</template></el-table-column>
        <el-table-column prop="details" label="结果" min-width="240" show-overflow-tooltip />
      </el-table>
    </div>
  </div>
</template>
<script setup>
import { reactive, ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { http } from '../plugins/axios'
import { authErrorMessage } from '../auth'
const settings=reactive({backupEnabled:true,backupAt:'03:00',cleanupEnabled:true,cleanupAt:'04:00',retentionDays:14})
const loading=ref(false),saving=ref(false),running=ref(''),entries=ref([])
async function refresh(){
  loading.value=true
  try {Object.assign(settings,(await http.get('/api/manage/backup/settings')).data);await history()}
  catch(e){ElMessage.error(authErrorMessage(e,'计划读取失败'))}
  finally{loading.value=false}
}
async function history(){
  try{entries.value=(await http.get('/api/manage/backup/history')).data}
  catch(e){ElMessage.error(authErrorMessage(e,'记录读取失败'))}
}
async function save(){
  if(saving.value)return
  if(!settings.backupAt||!settings.cleanupAt){ElMessage.warning('请选择执行时间');return}
  saving.value=true
  try{const {data}=await http.put('/api/manage/backup/settings',settings);ElMessage.success(data.message)}
  catch(e){ElMessage.error(authErrorMessage(e,'保存失败'))}
  finally{saving.value=false}
}
async function submit(type){
  if(type==='cleanup'){
    try{await ElMessageBox.confirm('确定按当前保留天数清理旧备份吗？清理后的文件无法直接恢复。','确认清理',{type:'warning',confirmButtonText:'确认清理',cancelButtonText:'取消'})}
    catch(_){return}
  }
  running.value=type
  try{const {data}=await http.post('/api/manage/backup/'+(type==='backup'?'run':'cleanup'));ElMessage.success(data.message);await history()}
  catch(e){ElMessage.error(authErrorMessage(e,'任务提交失败'))}
  finally{running.value=''}
}
function formatDate(value){return value?new Date(value).toLocaleString('zh-CN',{hour12:false}):'-'}
onMounted(refresh)
</script>
<style scoped>
.page{max-width:1050px}h1{font-size:27px;margin:0}h3{font-size:17px;margin:0}.heading,.heading-small{display:flex;align-items:center;justify-content:space-between;gap:15px}.heading{margin-bottom:23px}.heading p,.hint,.setting-row p{font-size:13px;color:#697e70;line-height:1.8}.panel{padding:25px;background:#fff;border-radius:14px;border:1px solid #e6ece6;margin-bottom:18px}.form{max-width:550px}.setting-row{display:flex;align-items:center;justify-content:space-between;margin:22px 0 13px;gap:15px}.setting-row strong{font-size:14px}.setting-row p{margin:6px 0 0}.inline-note{margin-left:10px}.action-buttons{display:flex;gap:9px;flex-wrap:wrap}.record-table{margin-top:20px;max-width:100%}@media(max-width:620px){.heading,.heading-small{flex-wrap:wrap}.panel{padding:18px}}
</style>
